import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import CrisisResources from './CrisisResources';
import { trackContact } from '../utils/metaPixel';

/**
 * "Ask the real Pastor Rob": sends this question + answer to Rob's queue in
 * the admin portal. Contact details are optional — without them Rob can read
 * it but not reply — so we ask warmly and once.
 *
 *   minor   they said they are under 18 or still in middle or high school
 *           (utils/askRob.js): no way to reach them is asked for; a trusted
 *           adult and The Net's Student Ministry instead (the Net Team spec's
 *           rule: under 18, nothing stored; the server keeps none either)
 *   abuse   abuse came up in this conversation: the team reaches them only
 *           the way they leave here, so leave only what is safe
 *   crisis  the card the note brought up (functions/feedback.js reads it the
 *           way Contact us reads a message), shown here, sent or not
 *
 * The 988 line is always here, and the privacy words say what is true: the
 * note goes to the safety team if it sounds like someone may be in danger.
 * TODO(BRAND_SPEC §20.2 decision 14): "Rob usually replies within a week",
 * once Rob decides the reply time.
 */
export const LINE_988 = "If you're in danger, call 911. If you're struggling, call or text 988 now.";
export const STUDENTS = 'Since you’re under 18, we don’t take numbers or emails here. Talk with a parent or another adult you trust; they can reach The Net’s Student Ministry for you at thenet.org/contact.';
export const SAFE_WAY = 'If anyone else could see your phone or email, leave only a way that’s safe for you, or none. We’ll only use the way you leave here.';

function AskRobPanel({ user, alreadySent, onCancel, onSend, focusOnOpen = false, minor = false, abuse = false, crisis = null }) {
  const [name, setName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.isAnonymous ? '' : (user?.email || ''));
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!focusOnOpen || !rootRef.current) return;
    const field = rootRef.current.querySelector('input, textarea, button');
    if (field) field.focus({ preventScroll: true });
  }, [focusOnOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    // Under 18: nothing to reach them by, and no note (it could hold a number).
    const contact = !minor && (name || email || phone) ? { name: name.trim(), email: email.trim(), phone: phone.trim(), preferred: phone.trim() && !email.trim() ? 'text' : email.trim() ? 'email' : 'any' } : null;
    const ok = await onSend({ contact, note: minor ? '' : note.trim() });
    setSending(false);
    if (!ok) setError("That didn't go through. Please try again in a moment.");
    else trackContact(); // Meta "Contact", no parameters (utils/metaPixel.js); the Contact us page adds the same line
  };

  const card = crisis ? <div className="askrob-card"><CrisisResources category={crisis} /></div> : null;

  if (alreadySent) {
    return (
      <div className="askrob-panel sent" role="status" ref={rootRef}>
        {card}
        <p className="askrob-sent">
          <Icon name="check" size={20} />
          <span>
            <strong>Sent to Pastor Rob.</strong>{' '}
            {minor
              ? STUDENTS
              : <>Pastor Rob or someone on his team reads every one. {email || phone ? 'He has a way to reach you.' : 'Add a way to reach you next time if you want a reply.'}</>}
          </span>
        </p>
        <p className="askrob-988">{LINE_988}</p>
        <div className="askrob-actions">
          <button type="button" className="apr-btn apr-btn--quiet" onClick={onCancel}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <form className="askrob-panel" onSubmit={submit} ref={rootRef} aria-labelledby="askrob-title">
      {card}
      <div className="askrob-head">
        <h3 id="askrob-title" className="askrob-title">Send this to the real Pastor Rob</h3>
        {minor ? (
          <p>He’ll see this conversation — your questions and the answers. {STUDENTS}</p>
        ) : (
          <p>He'll see this conversation — your questions and the answers. Leave a way to reach you if you'd like him to follow up; it's optional, and it stays between you and the church, unless someone's safety is at risk.</p>
        )}
        {abuse && !minor && <p className="askrob-safe">{SAFE_WAY}</p>}
      </div>
      {!minor && (
        <div className="askrob-grid">
          <label>
            <span>Your name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="given-name" placeholder="First name is plenty" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} autoComplete="email" placeholder="you@example.com" />
          </label>
          <label>
            <span>Phone</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="tel" placeholder="(256) 555-0100" />
          </label>
          <label className="askrob-wide">
            <span>Anything you'd like Rob to know? <em>(optional)</em></span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} rows={2} placeholder="What's behind the question, or how he can pray for you" />
          </label>
        </div>
      )}
      <p className="askrob-988">{LINE_988}</p>
      {error && (
        <p className="apr-error askrob-error" role="alert">
          <Icon name="triangle-alert" size={16} />
          <span>{error}</span>
        </p>
      )}
      <div className="askrob-actions">
        <button type="button" className="apr-btn apr-btn--quiet" onClick={onCancel} disabled={sending}>Cancel</button>
        <button type="submit" className="apr-btn" disabled={sending}>{sending ? 'Sending…' : 'Send to Rob'}</button>
      </div>
    </form>
  );
}

export default AskRobPanel;
